import * as vp from 'vopay';
import {
  Box,
  Pagination,
  GetColorStatus,
  capitalizeFirstLetterOfWords,
  formattedCurrency,
  formatMaskedAccount,
  usePagination,
  useUpdateQueryParams,
} from 'vopay';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState, useEffect, useMemo } from 'react';
import type { DefaultOptionProps } from 'vopay';
import { format, parseISO } from 'date-fns';
import ScheduledTransactionsForm from '@/components/Transaction/ScheduledTransactions/ScheduledTransactionsForm';
import useFetchScheduledPayments, { toScheduledPaymentsFilters } from '@/hooks/useApi/useFetchScheduledPayments';
import { useClientData } from '@/providers/ClientData.provider';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import { useI18nLabels } from '@/utils/useI18nLabels';
import ActivityType from '../ActivityType';
import type { ActivityTabType } from '../ActivityTab';
import { TableFiltersContainer, TransactionStatus, Table, TableSkeleton } from 'UI';
import styles from '../ActivityTab.module.scss';

type QueryParams = {
  status?: string;
  scheduleType?: string;
  transactionType?: string;
  frequency?: vp.NameOfFrequency | 'all';
  searchTerm?: string;
  startDate?: string;
  endDate?: string;
  activityType?: ActivityTabType;
};

type TransactionParams = {
  status: string;
  searchTerm: string;
  startDate: string;
  endDate: string;
  frequency: vp.NameOfFrequency | 'all';
  scheduleType: string;
  transactionType: string;
  hasInvalidEndDate: boolean;
  offset?: number;
};

type ScheduledTransactionHistoryTableProps = {
  accountID: string;
  activityTypeOptions: DefaultOptionProps[];
  currentType: string;
  handleCurrentType: (val: ActivityTabType) => void;
};

const ScheduledTransactionHistoryTable: FunctionComponent<ScheduledTransactionHistoryTableProps> = ({
  accountID,
  activityTypeOptions,
  currentType,
  handleCurrentType,
}) => {
  const alertDialog = useAlertDialog();
  const { response } = useClientData();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactionsHistory' });
  const { frequencyLabelMapper, paymentTypeLabelMapper, scheduledPaymentStatusLabelMapper } = useI18nLabels();
  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [filteredList, setFilteredList] = useState<vp.MergedScheduledPayment[]>([]);
  const [numTotalRecords, setNumTotalRecords] = useState(0);
  const { fetching, fetchScheduledPayments } = useFetchScheduledPayments();

  const queryParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const initialStartDate = moment().subtract(7, 'day').format('YYYY-MM-DD');
  const initialEndDate = moment().format('YYYY-MM-DD');
  const status = useMemo(() => queryParams.get('status') || '', [queryParams]);
  const searchTerm = useMemo(() => queryParams.get('searchTerm') || '', [queryParams]);
  const startDate = useMemo(() => queryParams.get('startDate') || initialStartDate || '', [queryParams]);
  const endDate = useMemo(() => queryParams.get('endDate') || initialEndDate || '', [queryParams]);

  const frequency = useMemo(() => (queryParams.get('frequency') as vp.NameOfFrequency | 'all') || '', [queryParams]);

  const scheduleType = useMemo(() => queryParams.get('scheduleType') || '', [queryParams]);
  const transactionType = useMemo(() => queryParams.get('transactionType') || '', [queryParams]);

  const filtersApplied = useMemo(() => {
    return Boolean(
      status ||
        searchTerm ||
        frequency ||
        transactionType ||
        scheduleType ||
        (startDate && startDate !== initialStartDate) ||
        (endDate && endDate !== initialEndDate),
    );
  }, [
    status,
    searchTerm,
    frequency,
    transactionType,
    scheduleType,
    startDate,
    endDate,
    initialStartDate,
    initialEndDate,
  ]);

  const updateQueryParams = useUpdateQueryParams<QueryParams>();

  const PER_PAGE = 20;

  const { pageCount, pageIndex, setPageIndex } = usePagination(numTotalRecords, {
    pageSize: PER_PAGE,
  });

  const fetchScheduledTransactionHistory = async ({
    startDate,
    endDate,
    status,
    hasInvalidEndDate,
    searchTerm,
    frequency,
    scheduleType,
    transactionType,
    offset = 0,
  }: TransactionParams) => {
    if (hasInvalidEndDate) {
      setShouldShowEndDateError(true);

      return;
    }

    const request: vp.ScheduledPaymentsRequest = {
      StartDate: startDate,
      EndDate: endDate,
      SearchText: searchTerm,
      Limit: PER_PAGE,
      Offset: offset,
      // The legacy endpoint reads ClientAccountID ?? ContactID into the same
      // filter, so contact-based accounts are covered by ClientAccountID too
      ClientAccountID: accountID,
      ...toScheduledPaymentsFilters({ scheduleType, transactionType, frequency, status }),
    };

    try {
      const res = await fetchScheduledPayments(request);
      setFilteredList(res.scheduledPayments);
      setNumTotalRecords(res.numTotalRecords);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('ErrorMessages.fetchTransactions');
      alertDialog.error(t('ErrorMessages.error'), msg);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);

    fetchScheduledTransactionHistory({
      startDate,
      endDate,
      status,
      hasInvalidEndDate: false,
      searchTerm,
      frequency,
      scheduleType,
      transactionType,
      offset,
    });

    updateQueryParams({
      activityType: 'Scheduled Transactions',
    });
  }, []);

  useEffect(() => {
    setHasInvalidEndDate(startDate > endDate);
  }, [startDate, endDate]);

  const tableProps: vp.TableProps<vp.MergedScheduledPayment> = {
    id: 'sheduled-transactions-list',
    version: '2',
    zebra: true,
    columns: [
      {
        id: 'id',
        header: t('Columns.id'),
        align: 'left',
        template: ({ data }) => {
          return (
            <Table.Link
              to={{
                pathname: `/scheduled-transactions/details/${data.ID}`,
                state: {
                  transactionId: data.ID,
                  // Legacy and new IDs are independent auto-increments; the
                  // details page uses this to query the right source
                  isLegacy: data.IsLegacy,
                },
              }}
            >
              {data.ID}
            </Table.Link>
          );
        },
      },
      {
        id: 'name',
        header: t('Columns.name'),
        align: 'left',
        template: ({ data }) => data.TokenData.FullName || '',
      },
      {
        id: 'scheduleType',
        header: t('Columns.scheduleType'),
        align: 'left',
        template: ({ data }) =>
          t(`SearchForm.Options.scheduleTypeOptions.${data.ScheduleType}`, {
            defaultValue: capitalizeFirstLetterOfWords(data.ScheduleType),
          }),
      },
      {
        id: 'transactionType',
        header: t('Columns.transactionType'),
        align: 'left',
        template: ({ data }) => paymentTypeLabelMapper(data.PaymentType),
      },
      {
        id: 'frequency',
        header: t('Columns.frequency'),
        align: 'left',
        template: ({ data }) =>
          data.Repeats ? frequencyLabelMapper(data.Repeats) || capitalizeFirstLetterOfWords(data.Repeats) : '',
      },
      {
        id: 'amount',
        header: t('Columns.amount'),
        align: 'left',
        template: ({ data }) => (
          <>
            ${formattedCurrency(data.Amount)} {response.Country === 'CA' ? 'CAD' : 'USD'}
          </>
        ),
      },
      {
        id: 'startDate',
        header: t('Columns.startDate'),
        align: 'left',
        // Date-only: new rows carry a datetime while legacy rows carry a date,
        // so the raw values would render inconsistently in one column
        template: ({ data }) => data.StartDateTime.slice(0, 10),
      },
      {
        id: 'ending',
        header: t('Columns.ending'),
        align: 'left',
        template: ({ data }) =>
          data.ScheduleEndDate
            ? data.ScheduleEndDate
            : data.NumberOfPayments !== ''
              ? t('endingAfterPayments', {
                  defaultValue: 'Ending After {{counts}} Payments',
                  counts: data.NumberOfPayments,
                })
              : '',
      },
      {
        id: 'account',
        header: t('Columns.account'),
        align: 'left',
        template: ({ data }) =>
          formatMaskedAccount(data.TokenData.MaskedAccount || data.TokenData.CreditCardNumber || ''),
      },
      {
        id: 'status',
        header: t('Columns.status'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" ai="center">
            <TransactionStatus
              status={GetColorStatus(data.Status)}
              label={scheduledPaymentStatusLabelMapper(data.Status)}
            />
          </Box>
        ),
      },
    ],
    rows: filteredList.map((item): { id: string; data: vp.MergedScheduledPayment } => ({
      // Source-prefixed so a legacy/new ID collision can't duplicate row keys
      id: item.IsLegacy ? `legacy-${item.ID}` : String(item.ID || ''),
      data: item,
    })),
  };

  return (
    <Box flx="col">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="sb" ai="center">
          <ActivityType
            activityTypeOptions={activityTypeOptions}
            currentType={currentType}
            handleCurrentType={handleCurrentType}
          />

          <TableFiltersContainer.Trigger />
        </Box>

        <TableFiltersContainer.Content>
          <ScheduledTransactionsForm
            timeRange={[moment(startDate).valueOf() as vp.Timestamp, moment(endDate).valueOf() as vp.Timestamp]}
            scheduleType={scheduleType}
            frequency={frequency}
            status={status}
            searchTerm={searchTerm}
            transactionType={transactionType}
            hasInvalidEndDate={hasInvalidEndDate}
            shouldShowEndDateError={shouldShowEndDateError}
            setShouldShowEndDateError={setShouldShowEndDateError}
            initialTimeRange={[
              parseISO(startDate).getTime() as vp.Timestamp,
              parseISO(endDate).getTime() as vp.Timestamp,
            ]}
            search={(searchParams) => {
              updateQueryParams({
                status: searchParams.status,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                scheduleType: searchParams.scheduleType,
                transactionType: searchParams.transactionType,
                frequency: searchParams.frequency,
              });

              fetchScheduledTransactionHistory({
                status: searchParams.status,
                searchTerm: searchParams.searchTerm,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
                scheduleType: searchParams.scheduleType,
                transactionType: searchParams.transactionType,
                frequency: searchParams.frequency,
                hasInvalidEndDate: searchParams.timeRange[0] > searchParams.timeRange[1],
              });
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>

      <div className={styles.tableContainer}>{fetching ? <TableSkeleton /> : <Table {...tableProps} />}</div>

      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);

              fetchScheduledTransactionHistory({
                startDate,
                endDate,
                status,
                hasInvalidEndDate: startDate > endDate,
                searchTerm,
                frequency,
                scheduleType,
                transactionType,
                offset,
              });
            }}
          />
        )}
      </Box>

      {!fetching && filteredList.length === 0 && (
        <Box flx="col" jc="center">
          <Box ai="center" jc="center" pt="md">
            {t('noRecordMessage')}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default ScheduledTransactionHistoryTable;
